import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageLoader from '../PageLoader/PageLoader';
import usePageLoader from '../../hooks/usePageLoader';
import './PersonalInformation.css';
import { getErrorMessage } from '../../utils/errorUtils';

/**
 * PersonalInformation component - Displays and allows editing of user's personal information.
 * NOTE: Rendered inside AccountLayout, so no page wrapper or sidebar needed here.
 */
function PersonalInformation() {
    const { currentUser, getUserProfile, updateUserProfile } = useAuth();
    const [userData, setUserData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        contactNumber: ''
    });
    const [originalData, setOriginalData] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [actionError, setActionError] = useState(null);

    // ── Page-level overlay: shows immediately on mount, hides once data is ready ──
    const { pageReady, markPageReady } = usePageLoader();

    const fetchUserData = useCallback(async () => {
        setLoading(true);
        const data = await getUserProfile(currentUser.uid);
        const profileData = {
            firstName: data?.firstName || '',
            lastName: data?.lastName || '',
            email: currentUser?.email || '',
            contactNumber: data?.contactNumber || ''
        };
        setUserData(profileData);
        setOriginalData(profileData);
        setLoading(false);
        markPageReady(); // ← signal overlay to hide
    }, [currentUser, getUserProfile, markPageReady]);

    useEffect(() => {
        if (currentUser) {
            fetchUserData();
        }
    }, [currentUser, fetchUserData]);

    const handleEdit = () => setIsEditing(true);

    const handleCancel = () => {
        setUserData(originalData);
        setIsEditing(false);
    };

    const handleSave = async () => {
        setActionError(null);
        setSaving(true);
        try {
            await updateUserProfile(currentUser.uid, {
                firstName: userData.firstName,
                lastName: userData.lastName
            });
            setOriginalData(userData);
            setIsEditing(false);
            setShowSuccessDialog(true);
        } catch (error) {
            console.error('Error updating profile:', error);
            setActionError(getErrorMessage(error, 'Failed to update personal information. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        setUserData(prev => ({ ...prev, [field]: value }));
    };

    return (
    <>
        {showSuccessDialog && (
            <div className="pi-dialog-overlay">
                <div className="pi-dialog">
                    <div className="pi-dialog-icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                        </svg>
                    </div>
                    <p className="pi-dialog-title">Information Updated</p>
                    <p className="pi-dialog-msg">Your personal information has been updated successfully!</p>
                    <button className="pi-dialog-btn" onClick={() => setShowSuccessDialog(false)}>Done</button>
                </div>
            </div>
        )}
        {/* ── Global page overlay — shown until data arrives ── */}
            {!pageReady && <PageLoader message="Loading your information…" />}

            {/* ── Per-data spinner (original, kept as-is) ── */}
            {loading ? (
                <div className="personal-info-card">
                    <div className="ph-loading">
                        <div className="ph-spinner"/>
                        <span>Loading your information…</span>
                    </div>
                </div>
            ) : (
                <div className="personal-info-card">
                    {actionError && (
                        <div className="pi-error-banner" onClick={() => setActionError(null)}>
                            {actionError}
                        </div>
                    )}
                    <div className="card-header">
                        <h2 className="card-title">Personal Information</h2>
                        {!isEditing ? (
                            <button
                                className="edit-button"
                                onClick={handleEdit}
                                aria-label="Edit personal information"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                                Edit
                            </button>
                        ) : (
                            <div className="button-group">
                                <button className="cancel-button" onClick={handleCancel} disabled={saving}>
                                    Cancel
                                </button>
                                <button className="save-button" onClick={handleSave} disabled={saving}>
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        )}
                    </div>

                    <form className="info-form" onSubmit={(e) => e.preventDefault()}>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="firstName" className="form-label">First Name</label>
                                <input
                                    id="firstName"
                                    type="text"
                                    className={`form-input ${!isEditing ? 'readonly' : ''}`}
                                    value={userData.firstName}
                                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                                    placeholder="first name"
                                    readOnly={!isEditing}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="lastName" className="form-label">Last Name</label>
                                <input
                                    id="lastName"
                                    type="text"
                                    className={`form-input ${!isEditing ? 'readonly' : ''}`}
                                    value={userData.lastName}
                                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                                    placeholder="last name"
                                    readOnly={!isEditing}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="email" className="form-label">Email Address</label>
                                <input
                                    id="email"
                                    type="email"
                                    className="form-input readonly"
                                    value={userData.email}
                                    placeholder="example@gmail.com"
                                    readOnly
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="contactNumber" className="form-label">Contact Number</label>
                                <input
                                    id="contactNumber"
                                    type="tel"
                                    className="form-input readonly"
                                    value={userData.contactNumber}
                                    placeholder="09123456789"
                                    readOnly
                                />
                            </div>
                        </div>
                    </form>
                </div>
            )}
        </>
    );
}

export default PersonalInformation;